<template>
    <div class="ui-page">
        <ui-page-header id="title" title="Habilitações/Divergências" subtitle="Pedidos de habilitação e divergência de crédito apresentados pelos credores.">
            <!-- Acoes secundarias agrupadas; "Incluir" fica visivel -->
            <ui-action-menu :items="[
                { text: 'Exportar Excel', icon: 'mdi-microsoft-excel', description: `Pedidos com os filtros aplicados (${dataList.count})`, loading: exportLoading, disabled: dataList.loading, handler: exportXlsx },
                { divider: true },
                { header: 'Em massa' },
                { text: 'Prazo contraditório', icon: 'mdi-calendar-clock', handler: actionBulkDeadline },
                { text: 'Atribuir responsável', icon: 'mdi-account-plus-outline', handler: actionAssign },
                { text: 'Substituir responsável', icon: 'mdi-account-switch-outline', handler: actionReplace },
                { text: 'Análise em massa', icon: 'mdi-file-search-outline', id: 'btnMassAnalysis', to: '/analise-massa' },
                { divider: true },
                { text: 'Incluir em lote', icon: 'mdi-file-multiple-outline', id: 'btnBulkCreate', to: '/divergencias/lote' },
            ]" />
            <v-btn v-if="$allowed('event.launch')" id="btnNew" type="button" color="primary" depressed to="/event-create">
                <v-icon left small>mdi-plus</v-icon>
                Incluir
            </v-btn>
        </ui-page-header>

        <!-- Filtros rapidos -->
        <div class="ui-list-bar">
            <div class="d-flex align-center flex-wrap" style="gap: 4px 12px;">
                <span class="ui-muted ui-small">
                    <v-icon small class="mr-1">mdi-filter-variant</v-icon>Filtros rápidos
                </span>

                <v-chip-group v-model="quickFilters.status">
                    <v-chip filter small :value="0" color="primary" outlined @click="toggleFilter('status', 0)">Pendente</v-chip>
                    <v-chip filter small :value="1" color="primary" outlined @click="toggleFilter('status', 1)">Aprovado</v-chip>
                    <v-chip filter small :value="3" color="primary" outlined @click="toggleFilter('status', 3)">Aprov. Parcial</v-chip>
                    <v-chip filter small :value="2" color="primary" outlined @click="toggleFilter('status', 2)">Rejeitado</v-chip>
                </v-chip-group>

                <v-divider vertical class="my-2"></v-divider>

                <v-chip-group v-model="quickFilters.type">
                    <v-chip filter small :value="1" color="primary" outlined @click="toggleFilter('type', 1)">Habilitação</v-chip>
                    <v-chip filter small :value="2" color="primary" outlined @click="toggleFilter('type', 2)">Divergência</v-chip>
                </v-chip-group>

                <v-divider vertical class="my-2"></v-divider>

                <v-chip-group v-model="quickFilters.responsibility">
                    <v-chip filter small value="with" color="primary" outlined @click="toggleFilter('responsibility', 'with')">
                        Com Responsável
                    </v-chip>
                    <v-chip filter small value="without" color="primary" outlined @click="toggleFilter('responsibility', 'without')">
                        Sem Responsável
                    </v-chip>
                </v-chip-group>

                <v-divider vertical class="my-2"></v-divider>

                <v-chip-group v-model="quickFilters.archived">
                    <v-chip filter small :value="1" color="primary" outlined @click="toggleFilter('archived', 1)">
                        Arquivados
                    </v-chip>
                </v-chip-group>
            </div>

            <v-btn v-if="hasActiveFilters" text small class="ui-toolbar__clear" @click="clearQuickFilters">
                Limpar Filtros
                <v-icon right small>mdi-close</v-icon>
            </v-btn>
        </div>

        <ui-table-card :more-count="moreFiltersCount" clearable :can-clear="hasFilters" @clear="clearFilters">
            <template #toolbar>
                <v-text-field id="name" v-model="dataList.filter.name" placeholder="Buscar pelo nome" prepend-inner-icon="mdi-magnify"
                    dense outlined hide-details class="ui-toolbar__search" @input="filterDebounced" @keyup.enter="applyFilters"></v-text-field>
                <v-text-field id="id" v-model="dataList.filter.id" placeholder="Nº Pedido" dense outlined hide-details
                    class="ui-toolbar__field" @input="filterDebounced" @keyup.enter="applyFilters"></v-text-field>
                <v-autocomplete v-model="dataList.filter.client_id" :items="dataset.clients" item-value="id" item-text="name"
                    label="Caso" no-data-text="Nenhum caso encontrado" dense outlined hide-details class="ui-toolbar__field"
                    :menu-props="{ offsetY: true }" @change="applyFilters"></v-autocomplete>
                <v-select v-model="dataList.filter.responsible" :items="dataset.adminList" item-value="id" item-text="name"
                    label="Responsável" dense outlined hide-details class="ui-toolbar__field"
                    :menu-props="{ offsetY: true }" @change="applyFilters"></v-select>

                <!-- Demais filtros: quebram para as linhas seguintes -->
            </template>
            <template #more>
                <v-text-field id="registration" v-model="dataList.filter.registration" placeholder="CPF/CNPJ" dense outlined hide-details
                    class="ui-toolbar__field" @input="filterDebounced" @keyup.enter="applyFilters"></v-text-field>
                <v-currency-field id="amount" v-model="dataList.filter.amount" type="number" label="Valor maior ou igual"
                    :allowNegative=false dense outlined hide-details class="ui-toolbar__field" @input="filterDebounced"
                    @keyup.enter="applyFilters"></v-currency-field>
                <v-select v-model="dataList.filter.type" :items="dataset.eventTypes" item-value="id" item-text="name"
                    label="Tipo" dense outlined hide-details class="ui-toolbar__field"
                    :menu-props="{ offsetY: true }" @change="applyFilters"></v-select>
                <v-autocomplete v-model="dataList.filter.creditor_type" :items="getFilterCreditorTypes()" item-text="name" item-value="id"
                    :label="dataList.filter.client_id ? 'Classe' : 'Classe (selecione um caso)'" :disabled="!dataList.filter.client_id"
                    no-data-text="Nenhuma classe encontrada" dense outlined hide-details class="ui-toolbar__field"
                    :menu-props="{ offsetY: true }" @change="applyFilters"></v-autocomplete>
                <v-select v-model="dataList.filter.contradictory" :items="dataset.statusContradictory" item-value="id" item-text="name"
                    label="Contraditório" dense outlined hide-details class="ui-toolbar__field"
                    :menu-props="{ offsetY: true }" @change="applyFilters"></v-select>
                <v-autocomplete v-model="dataList.filter.origin" :items="dataset.creditorOriginTypes" item-value="name" item-text="name"
                    :disabled="dataset.loading" label="Origem do crédito" dense outlined hide-details class="ui-toolbar__field"
                    :menu-props="{ offsetY: true }" @change="applyFilters"></v-autocomplete>
                <v-select v-model="dataList.filter.has_process" :items="dataset.hasProcess" item-value="id" item-text="name"
                    label="Processo" dense outlined hide-details class="ui-toolbar__field"
                    :menu-props="{ offsetY: true }" @change="applyFilters"></v-select>
                <v-select v-model="dataList.filter.process_uf" :items="dataset.ufList" item-value="name" item-text="name"
                    label="UF processo" dense outlined hide-details class="ui-toolbar__field"
                    :menu-props="{ offsetY: true }" @change="applyFilters"></v-select>
                <v-select v-model="dataList.filter.has_invalid_doc_id" :items="dataset.hasInvalidDocId" item-value="id" item-text="name"
                    label="Doc ID inválido" dense outlined hide-details class="ui-toolbar__field"
                    :menu-props="{ offsetY: true }" @change="applyFilters"></v-select>
                <v-select v-model="dataList.filter.procuration" :items="dataset.procurationOptions" item-value="id" item-text="name"
                    label="Procuração do procurador" dense outlined hide-details class="ui-toolbar__field"
                    :menu-props="{ offsetY: true }" @change="applyFilters"></v-select>
                <v-select v-model="dataList.filter.late_filing" :items="dataset.lateFilingOptions" item-value="id" item-text="name"
                    label="Retardatário" dense outlined hide-details class="ui-toolbar__field"
                    :menu-props="{ offsetY: true }" @change="applyFilters"></v-select>
                <v-text-field id="process_number" v-model="dataList.filter.process_number" placeholder="Número processo" dense outlined hide-details
                    class="ui-toolbar__field" @input="filterDebounced" @keyup.enter="applyFilters"></v-text-field>
            </template>

            <v-data-table :headers="dataList.headers" :items="dataList.data" :server-items-length="dataList.count"
                :options.sync="dataList.options" :loading="dataList.loading" disable-sort hide-default-footer
                no-data-text="Nenhuma informação foi encontrada" loading-text="Carregando dados..." class="ui-clickable-rows"
                @click:row="openRow">

                <template v-slot:[`item.id`]="{ item }">
                    <div class="d-flex align-center">
                        <span>{{ item.id }}</span>
                        <v-tooltip bottom>
                            <template #activator="{ on }">
                                <v-icon v-if="item.is_complex" v-on="on" color="warning" class="ml-1" small>
                                    mdi-alert-outline
                                </v-icon>
                            </template>
                            <span>Item complexo</span>
                        </v-tooltip>

                        <v-tooltip bottom>
                            <template #activator="{ on }">
                                <v-icon v-if="item.review_later" v-on="on" color="primary" class="ml-1" small>
                                    mdi-history
                                </v-icon>
                            </template>
                            <span>Sinalizado para revisão posterior</span>
                        </v-tooltip>
                    </div>
                </template>

                <template v-slot:[`item.user.name`]="{ item }">
                    <span class="ui-cell-main">{{ item.user.name }}</span>
                    <v-tooltip v-if="item.procuration && Number(item.procuration.status) !== 1" bottom>
                        <template #activator="{ on }">
                            <v-icon v-on="on" small class="ml-1" style="cursor: pointer"
                                :color="Number(item.procuration.status) === 2 ? 'error' : 'warning'"
                                @click.stop="openProcurator(item.procuration)">
                                mdi-alert-circle
                            </v-icon>
                        </template>
                        <span>{{ procurationWarning(item.procuration) }} Clique para abrir as procurações.</span>
                    </v-tooltip>
                    <v-tooltip v-if="hasContradictory(item)" bottom>
                        <template #activator="{ on }">
                            <v-icon v-on="on" small class="ml-1" :color="Number(item.contradictory_status) === 2 ? 'error' : 'success'">
                                mdi-message-reply-text
                            </v-icon>
                        </template>
                        <span>{{ contradictoryTooltip(item) }}</span>
                    </v-tooltip>
                    <v-tooltip v-else-if="contradictoryOverdue(item)" bottom>
                        <template #activator="{ on }">
                            <v-icon v-on="on" small class="ml-1" color="grey">mdi-message-alert-outline</v-icon>
                        </template>
                        <span>Prazo de contraditório vencido em {{ item.contradictory_deadline_at | defaultDate("DD/MM/YYYY") }} sem resposta da devedora.</span>
                    </v-tooltip>
                </template>

                <template v-slot:[`item.status`]="{ item }">
                    <ui-badge :text="getChipLabel(item)" :tone="getChipTone(item)" />
                </template>

                <template v-slot:[`item.created_at`]="{ item }">
                    {{ item.created_at | defaultDate("DD/MM/YYYY HH:mm") }}
                </template>

                <template v-slot:[`item.contradictory_deadline_at`]="{ item }">
                    <span v-if="item.contradictory_deadline_at && item.contradictory_deadline_at !== '0000-00-00 00:00:00'">
                        {{ item.contradictory_deadline_at | defaultDate("DD/MM/YYYY", false, true) }}</span>
                    <span v-else>N/A</span>
                </template>

                <template v-slot:[`item.type`]="{ item }">
                    <ui-badge :text="getEventTypeLabel(item.type)" :tone="getEventTypeTone(item.type)" />
                    <ui-badge v-if="Number(item.late_filing) === 1" text="Retardatário" tone="warning" class="ml-1" />
                </template>

                <template v-slot:[`item.admin`]="{ item }">
                    {{
                        item.admin && item.admin.name
                            ? item.admin.name
                            : item.analyzed_at !== "0000-00-00 00:00:00" && !item.admin
                                ? "Sistema"
                                : "N/A"
                    }}
                </template>

                <template v-slot:[`item.responsible`]="{ item }">
                    {{ item.responsible && item.responsible.name ? item.responsible.name : 'N/A' }}
                </template>

                <template v-slot:[`item.analyzed_at`]="{ item }">
                    <span v-if="item.analyzed_at && item.analyzed_at !== '0000-00-00 00:00:00'">
                        {{ item.analyzed_at | defaultDate("DD/MM/YYYY HH:mm") }}</span>
                    <span v-else>N/A</span>
                </template>

                <template v-slot:[`item.actions`]="{ item }">
                    <ui-row-actions :actions="[
                        { text: 'Visualizar informações', icon: 'mdi-eye-outline', hidden: $check('Event', 'PATCH'), disabled: !$check('Event', 'GET'), handler: () => viewItem(item) },
                        { text: 'Editar informações', icon: 'mdi-pencil-outline', disabled: !$check('Event', 'PATCH'), handler: () => openEventDetailsTab(item) },
                        { text: 'Direcionar para outro responsável', icon: 'mdi-share-outline', hidden: item.responsible_admin_id !== null, disabled: !$allowed('event.assign'), handler: () => redirectItem(item) },
                        { text: 'Remover Responsável', icon: 'mdi-refresh', danger: true, hidden: item.responsible_admin_id === null, disabled: !$allowed('event.assign'), handler: () => removeResponsible(item) },
                        { text: item.archived_at ? 'Desarquivar pedido' : 'Arquivar pedido', icon: item.archived_at ? 'mdi-archive-arrow-up-outline' : 'mdi-archive-arrow-down-outline', disabled: !$allowed('event.archive'), handler: () => item.archived_at ? unarchiveItem(item) : archiveItem(item) },
                    ]" />
                </template>
            </v-data-table>

            <template #footer>
                <ui-pagination :total="dataList.count" :page="dataList.options.page" :items-per-page="dataList.options.itemsPerPage"
                    :disabled="dataList.loading" @update:page="pageChanged" @update:itemsPerPage="setPageSize" />
                <!-- Ir direto para uma pagina -->
                <div class="d-flex align-center justify-end px-4 pb-3">
                    <v-text-field v-model.number="targetPage" type="number" min="1" :max="maxPage" placeholder="Página"
                        hide-details dense outlined class="mx-2" style="max-width: 96px;" @keyup.enter="goToPage"></v-text-field>
                    <v-btn text small :disabled="dataList.loading" @click="goToPage">Ir para página</v-btn>
                </div>
            </template>
        </ui-table-card>

        <v-dialog v-model="redirectDialog" max-width="520px">
            <v-card>
                <v-card-title>Direcionar responsável</v-card-title>
                <v-card-text>
                    <p class="text-caption mb-4">
                        Selecione o administrador responsável pelo pedido #{{ redirectForm.item ? redirectForm.item.id : '' }}.
                    </p>
                    <v-autocomplete
                        v-model="redirectForm.responsible"
                        :items="dataset.admins"
                        item-text="name"
                        item-value="id"
                        label="Responsável"
                        no-data-text="Nenhum administrador encontrado"
                        outlined
                        dense
                        hide-details="auto"
                    />
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text color="grey lighten-1" @click="closeRedirectDialog">Cancelar</v-btn>
                    <v-btn v-if="$allowed('event.assign')"
                        color="primary"
                        :loading="redirectForm.loading"
                        :disabled="!redirectForm.responsible"
                        @click="saveRedirect"
                    >
                        Direcionar
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="bulkDeadlineDialog" max-width="760px" scrollable>
            <v-card>
                <v-card-title>Atribuir prazo de contraditório em massa</v-card-title>
                <v-divider></v-divider>
                <v-card-text style="height: 620px;">
                    <v-alert dense outlined type="info" class="mt-4">
                        O prazo será aplicado aos pedidos de Hab/Div que corresponderem aos filtros abaixo.
                    </v-alert>
                    <v-form ref="bulkDeadlineForm" class="mt-4">
                        <v-row>
                            <v-col cols="12">
                                <v-text-field v-model="bulkDeadlineForm.deadline" label="Prazo contraditório" type="date" outlined dense hide-details="auto" />
                            </v-col>
                            <v-col cols="12">
                                <v-autocomplete
                                    v-model="bulkDeadlineForm.client_id"
                                    :items="dataset.clients"
                                    item-text="name"
                                    item-value="id"
                                    label="Caso (Filtro)"
                                    placeholder="Selecione um caso para filtrar"
                                    outlined dense
                                    hide-details="auto"
                                    clearable
                                ></v-autocomplete>
                            </v-col>
                            <v-col cols="6">
                                <v-text-field v-model="bulkDeadlineForm.totalOrders" label="Total de pedidos" type="number" outlined dense hide-details="auto" hint="Deixe vazio para todos" persistent-hint />
                            </v-col>
                            <v-col cols="6">
                                <v-select v-model="bulkDeadlineForm.type" :items="[{text:'Todos', value:null}, {text:'Habilitação', value:1}, {text:'Divergência', value:2}]" label="Tipo" outlined dense hide-details="auto" />
                            </v-col>
                            <v-col cols="6">
                                <v-select v-model="bulkDeadlineForm.class" :items="dataset.claimClasses" label="Classe" item-text="name" item-value="id" outlined dense hide-details="auto" />
                            </v-col>
                            <v-col cols="6">
                                <v-select v-model="bulkDeadlineForm.status" :items="dataset.eventStatus" label="Status" item-text="name" item-value="id" outlined dense hide-details="auto" />
                            </v-col>
                            <v-col cols="6">
                                <v-text-field v-model="bulkDeadlineForm.minValue1" label="Valor igual ou maior" prefix="R$" outlined dense hide-details="auto" />
                            </v-col>
                            <v-col cols="6">
                                <v-text-field v-model="bulkDeadlineForm.minValue2" label="Valor igual ou menor" prefix="R$" outlined dense hide-details="auto" />
                            </v-col>
                            <v-col cols="6">
                                <v-select v-model="bulkDeadlineForm.hasProcess" :items="[{text:'Todos', value:null}, {text:'Sim', value:1}, {text:'Não', value:0}]" label="Processo" outlined dense hide-details="auto" />
                            </v-col>
                            <v-col cols="6">
                                <v-select v-model="bulkDeadlineForm.courtUf" :items="dataset.states" item-text="nome" item-value="sigla" label="Tribunal UF" :disabled="bulkDeadlineForm.hasProcess !== 1" outlined dense hide-details="auto" />
                            </v-col>
                            <v-col cols="12">
                                <v-select v-model="bulkDeadlineForm.businessOrigin" :items="['Compra de passagem', 'Reserva de hotel', 'Nota fiscal', 'Duplicatas', 'Reclamatória trabalhista', 'Contrato de trabalho', 'Outros negócios']" label="Origem de negócio" outlined dense hide-details="auto" />
                            </v-col>
                            <v-col cols="6">
                                <v-select v-model="bulkDeadlineForm.contradictory" :items="[{text:'Todos', value:null}, {text:'Sim', value:1}, {text:'Não', value:0}]" label="Contraditório respondido" outlined dense hide-details="auto" />
                            </v-col>
                            <v-col cols="6">
                                <v-select v-model="bulkDeadlineForm.hasDeadline" :items="[{text:'Todos', value:null}, {text:'Com prazo', value:1}, {text:'Sem prazo', value:0}]" label="Prazo atual" :disabled="!!bulkDeadlineForm.deadlineOn" outlined dense hide-details="auto" />
                            </v-col>
                            <!-- Prorrogacao seletiva: so os pedidos cujo prazo atual cai neste dia -->
                            <v-col cols="12">
                                <v-text-field v-model="bulkDeadlineForm.deadlineOn" label="Somente pedidos com prazo atual no dia" type="date" outlined dense hide-details="auto" clearable
                                    hint="Ex.: para prorrogar apenas os contraditórios com prazo em 24/09, informe 24/09 aqui e o novo prazo acima." persistent-hint
                                    @input="onBulkDeadlineOnChange" />
                            </v-col>
                            <!-- Aviso: o campo e o dia do PRAZO atual, nao a data do pedido -->
                            <v-col v-if="bulkDeadlineForm.deadlineOn" cols="12">
                                <v-alert dense outlined type="warning" class="mb-0">
                                    <strong>Atenção:</strong> a data informada é o <strong>dia do prazo atual</strong> do contraditório
                                    ({{ bulkDeadlineForm.deadlineOn.split('-').reverse().join('/') }}), e não o dia em que o pedido foi feito.
                                    Somente os pedidos cujo prazo vence exatamente nesse dia serão alterados
                                    <template v-if="bulkDeadlineForm.deadline">para {{ bulkDeadlineForm.deadline.split('-').reverse().join('/') }}</template>;
                                    os demais prazos do caso permanecem como estão.
                                </v-alert>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                    <v-spacer />
                    <v-btn color="grey lighten-1" text @click="bulkDeadlineDialog = false">Cancelar</v-btn>
                    <v-btn color="primary" @click="saveBulkDeadline" :loading="loading">Aplicar prazo</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="assignDialog" max-width="700px" scrollable>
            <v-card>
                <v-card-title>Atribuir Responsável</v-card-title>
                <v-divider></v-divider>
                <v-card-text style="height: 600px;">
                <v-form ref="assignForm" class="mt-4">
                    <v-row>
                    <v-col cols="12">
                        <v-autocomplete 
                            v-model="assignForm.client_id" 
                            :items="dataset.clients" 
                            item-text="name" 
                            item-value="id" 
                            label="Caso (Filtro)" 
                            placeholder="Selecione um caso para filtrar"
                            outlined dense 
                            hide-details="auto" 
                            clearable
                        ></v-autocomplete>
                    </v-col>

                    <v-col cols="6">
                        <v-text-field v-model="assignForm.totalOrders" label="Total de pedidos" type="number" outlined dense hide-details="auto" />
                    </v-col>
                    <v-col cols="6">
                        <v-select v-model="assignForm.class" :items="dataset.claimClasses" label="Classe" item-text="name" item-value="id" outlined dense hide-details="auto" />
                    </v-col>
                    <v-col cols="6">
                        <v-text-field v-model="assignForm.minValue1" label="Valor igual ou maior" prefix="R$" outlined dense hide-details="auto" />
                    </v-col>
                    <v-col cols="6">
                        <v-text-field v-model="assignForm.minValue2" label="Valor igual ou menor" prefix="R$" outlined dense hide-details="auto" />
                    </v-col>
                    <v-col cols="6">
                        <v-select v-model="assignForm.hasProcess" :items="[{text:'Todos', value:null}, {text:'Sim', value:1}, {text:'Não', value:0}]" label="Processo" outlined dense hide-details="auto" />
                    </v-col>
                    <v-col cols="6">
                        <v-select v-model="assignForm.courtUf" :items="dataset.states" item-text="nome" item-value="sigla" label="Tribunal UF" :disabled="assignForm.hasProcess !== 1" outlined dense hide-details="auto" />
                    </v-col>
                    <v-col cols="12">
                        <v-select v-model="assignForm.businessOrigin" :items="['Compra de passagem', 'Reserva de hotel', 'Nota fiscal', 'Duplicatas', 'Reclamatória trabalhista', 'Contrato de trabalho', 'Outros negócios']" label="Origem de negócio" outlined dense hide-details="auto" />
                    </v-col>
                    <v-col cols="6">
                        <v-select v-model="assignForm.contradictory" :items="[{text:'Todos', value:null}, {text:'Sim', value:1}, {text:'Não', value:0}]" label="Contraditório" outlined dense hide-details="auto" />
                    </v-col>
                    <v-col cols="6">
                        <v-select v-model="assignForm.responsible" :items="dataset.admins" label="Responsável" item-text="name" item-value="id" no-data-text="Nenhum administrador encontrado" outlined dense hide-details="auto" />
                    </v-col>
                    <v-col cols="12">
                        <v-checkbox v-model="assignForm.withoutResponsible" label="Apenas sem responsável" hide-details="auto" class="mt-0" />
                    </v-col>
                    </v-row>
                </v-form>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                <v-spacer />
                <v-btn color="grey lighten-1" text @click="assignDialog = false">Cancelar</v-btn>
                <v-btn v-if="$allowed('event.assign')" color="primary" @click="saveAssignment" :loading="loading">Salvar</v-btn>
                </v-card-actions>
            </v-card>
            </v-dialog>

            <v-dialog v-model="dialogReplace" max-width="600px">
            <v-card>
                <v-card-title class="text-h6">Substituir Responsável</v-card-title>
                <v-card-text>
                <p class="mb-4 text-caption">Transfira demandas de um responsável para outro filtrando por status e processo.</p>
                <v-form ref="replaceForm">
                    <v-row>
                    <v-col cols="12" md="6">
                        <v-text-field v-model="replaceForm.quantity" label="Quantidade" type="number" outlined dense hide-details="auto" hint="Deixe vazio para todos" persistent-hint />
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-select v-model="replaceForm.hasProcess" :items="[{text:'Todos', value:null}, {text:'Sim', value:1}, {text:'Não', value:0}]" label="Processo" outlined dense hide-details="auto" />
                    </v-col>
                    <v-col cols="12">
                        <v-select v-model="replaceForm.status" 
                        :items="[{text:'Todos', value:null}, {text:'Pendente', value:0}, {text:'Aprovado', value:2}, {text:'Rejeitado', value:1}]" 
                        label="Status das Demandas" outlined dense hide-details="auto" />
                    </v-col>
                    <v-col cols="12">
                        <v-select v-model="replaceForm.originResponsible" :items="dataset.admins" label="Responsável de Origem" item-text="name" item-value="id" outlined dense hide-details="auto" />
                    </v-col>
                    <v-col cols="12">
                        <v-select v-model="replaceForm.targetResponsible" :items="dataset.admins" label="Responsável de Destino" item-text="name" item-value="id" outlined dense hide-details="auto" />
                    </v-col>
                    </v-row>
                </v-form>
                </v-card-text>
                <v-card-actions>
                <v-spacer />
                <v-btn color="grey lighten-1" text @click="dialogReplace = false">Cancelar</v-btn>
                <v-btn v-if="$allowed('event.assign')" color="primary" @click="saveReplacement" :loading="loading" :disabled="!replaceForm.originResponsible || !replaceForm.targetResponsible">
                    Transferir
                </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import Vue from "vue";
import VueLodash from 'vue-lodash'
import _ from 'lodash';
import axios from 'axios';

import creditorTypes from "@/common/data/creditor-types.data";
import creditorClassUtil from "@/common/util/creditor-class.util";
import ufList from "@/common/data/uf-list.data";

import object from "@/common/util/object"
import eventStatus from "@/common/data/event-status.data";
import { downloadXlsx } from "@/common/util/export-xlsx.util";
import currencyTypes from "@/common/data/currency-types.data";

Vue.use(VueLodash)

export default {
    name: "UserList",

    data: () => ({
        exportLoading: false,
        user: null,
        targetPage: 1,
        assignDialog: false,
        bulkDeadlineDialog: false,
        redirectDialog: false,
        dialogReplace: false,
        loading: false,

        dataset: {
            eventStatus: [{ id: "", name: "Todos" }, ...eventStatus],
            eventTypes: [{ id: "", name: "Todos" }, { id: 1, name: "Habilitação" }, { id: 2, name: "Divergência" }, { id: 90, name: "Auditoria AGC/Zoom" }],
            hasProcess: [{ id: "", name: "Todos" }, { id: 1, name: "SIM" }, { id: 2, name: "NÃO" }],
            hasInvalidDocId: [{ id: "", name: "Todos" }, { id: 1, name: "SIM" }, { id: 2, name: "NÃO" }],
            // Pedidos apresentados por procurador, conforme a situacao da procuracao usada.
            // Pedido incluido depois do prazo de hab/div (art. 10 da LRF).
            lateFilingOptions: [
                { id: "", name: "Todos" },
                { id: "1", name: "Retardatários" },
                { id: "0", name: "No prazo" },
            ],
            procurationOptions: [
                { id: "", name: "Todos" },
                { id: "pending", name: "Pendente de análise" },
                { id: "rejected", name: "Rejeitada" },
                { id: "attention", name: "Pendente ou rejeitada" },
            ],
            hasContradictory: [{ id: "", name: "Todos" }, { id: 1, name: "SIM" }, { id: 2, name: "NÃO" }],
            // Situacao do contraditorio enviado pela devedora (filtro da busca).
            statusContradictory: [
                { id: "", name: "Todos" },
                { id: "received", name: "Recebido" },
                { id: "agreed", name: "Recebido - concordou" },
                { id: "disagreed", name: "Recebido - discordou" },
                { id: "pending", name: "Aguardando (prazo em aberto)" },
                { id: "overdue", name: "Prazo vencido sem resposta" },
                { id: "none", name: "Não recebido" },
            ],
            ufList: [{ id: "", name: "Todos" }, ...ufList],
            creditorTypes: creditorTypes,
            data: {},
            clients: [],
            admins: [], 
            
            claimClasses: [
                { id: 1, name: 'Trabalhista' }, 
                { id: 2, name: 'Garantia Real' },
                { id: 3, name: 'Quirografário' },
                { id: 4, name: 'ME/EPP' }
            ],
            states: [
                { sigla: 'SP', nome: 'São Paulo' }, 
                { sigla: 'RJ', nome: 'Rio de Janeiro' },
                { sigla: 'MG', nome: 'Minas Gerais' },
                { sigla: 'RS', nome: 'Rio Grande do Sul' },
                { sigla: 'BA', nome: 'Bahia' },
                { sigla: 'PR', nome: 'Paraná' },
                { sigla: 'PE', nome: 'Pernambuco' },
                { sigla: 'CE', nome: 'Ceará' },
                { sigla: 'PA', nome: 'Pará' },
                { sigla: 'SC', nome: 'Santa Catarina' },
                { sigla: 'GO', nome: 'Goiás' },
                { sigla: 'MA', nome: 'Maranhão' },
                { sigla: 'PB', nome: 'Paraíba' },
                { sigla: 'ES', nome: 'Espírito Santo' },
                { sigla: 'PI', nome: 'Piauí' },
                { sigla: 'RN', nome: 'Rio Grande do Norte' },
                { sigla: 'AL', nome: 'Alagoas' },
                { sigla: 'MT', nome: 'Mato Grosso' },
                { sigla: 'MS', nome: 'Mato Grosso do Sul' },
                { sigla: 'DF', nome: 'Distrito Federal' }], 
        },

        assignForm: {
            client_id: null, 
            totalOrders: null,
            class: null,
            minValue1: null,
            minValue2: null,
            hasProcess: null,
            courtUf: null,
            businessOrigin: null,
            contradictory: null,
            responsible: null,
            withoutResponsible: false
        },

        bulkDeadlineForm: {
            deadline: null,
            client_id: null,
            totalOrders: null,
            type: null,
            status: null,
            class: null,
            minValue1: null,
            minValue2: null,
            hasProcess: null,
            courtUf: null,
            businessOrigin: null,
            contradictory: null,
            hasDeadline: 0,
            deadlineOn: null,
        },

        replaceForm: {
            quantity: null,
            hasProcess: null,
            status: null,
            originResponsible: null,
            targetResponsible: null,
        },

        redirectForm: {
            item: null,
            responsible: null,
            loading: false,
        },

	        quickFilters: {
	            status: null,
	            type: null,
	            responsibility: null,
	            archived: null
	        },

        dataList: {
            filter: {
                name: "",
                status: "",
                type: "",
	                client_id: "",
	                responsibility: "",
	                archived: "",
	                procuration: "",
	                late_filing: ""
	            },
            headers: [
                { text: "ID", value: "id" },
                { text: "Credor", value: "user.name" },
                { text: "Caso", value: "client.name" },
                { text: "Tipo", value: "type" },
                { text: "Responsável", value: "responsible" },
                { text: "Solicitado em", value: "created_at" },
                { text: "Analisado por", value: "admin" },
                { text: "Analisado em", value: "analyzed_at" },
                { text: "Status", value: "status" },
                { text: '', value: 'actions', sortable: false, align: 'end', width: 56 },
            ],
            data: [],
            loading: false,
            count: 0,
            options: {
                page: 1,
                itemsPerPage: 10,
                sortBy: ['name'],
                sortDesc: [false],
            },
            footerOptions: {
                itemsPerPageText: 'Itens por página:',
                itemsPerPageOptions: [10, 20, 30, 40, 50],
                pageText: '{0}-{1} de {2}',
                // Botoes de primeira e ultima pagina no rodape da tabela
                showFirstLastPage: true,
                firstIcon: 'mdi-page-first',
                lastIcon: 'mdi-page-last',
            }
        },
    }),

    created() {
        this.user = this.$root.user ? (this.$root.user.data || this.$root.user) : null;
        this.load();
        this.loadClients();
        this.loadAdmins();
    },

    watch: {
        // Filtro escolhido no formulario reflete nos chips rapidos (e vice-versa via toggleFilter).
        'dataList.filter.status'(value) {
            this.quickFilters.status = [0, 1, 2, 3].includes(value) ? value : null;
        },
        'dataList.filter.type'(value) {
            this.quickFilters.type = [1, 2].includes(Number(value)) ? Number(value) : null;
        },
        'dataList.filter.client_id'(value, oldValue) {
            if (String(value) === String(oldValue)) return;

            this.loadCreditorClasses();
        },
    },

    computed: {
        // Filtros escondidos em "Mais filtros" que estao preenchidos.
        moreFiltersCount() {
            return [this.dataList.filter.registration, this.dataList.filter.amount, this.dataList.filter.type, this.dataList.filter.creditor_type, this.dataList.filter.contradictory, this.dataList.filter.origin, this.dataList.filter.has_process, this.dataList.filter.process_uf, this.dataList.filter.has_invalid_doc_id, this.dataList.filter.procuration, this.dataList.filter.late_filing, this.dataList.filter.process_number].filter((value) => value !== null && value !== undefined && value !== "" && value !== false && value !== 0).length;
        },
        hasFilters() {
            return Object.values(this.dataList.filter).some(value => value !== "" && value !== null && value !== undefined);
        },
        maxPage() {
            const size = this.dataList.options.itemsPerPage || 10;
            return Math.ceil(this.dataList.count / size) || 1;
        },
        hasActiveFilters() {
	            return this.quickFilters.status !== null ||
	                   this.quickFilters.type !== null ||
	                   this.quickFilters.responsibility !== null ||
	                   this.quickFilters.archived !== null;
        }
    },

    methods: {
        getEventTypeLabel(type) {
            if (Number(type) === 90) return "Auditoria AGC/Zoom"
            return Number(type) === 2 ? "Divergência" : "Habilitação"
        },

        getEventTypeColor(type) {
            if (Number(type) === 90) return "#6B7280"
            return Number(type) === 2 ? "#00BCD4" : "#009688"
        },

        // Tom do ui-badge equivalente as cores de getEventTypeColor.
        getEventTypeTone(type) {
            if (Number(type) === 90) return "neutral"
            return Number(type) === 2 ? "info" : "primary"
        },

        goToPage() {
            const page = Math.max(1, Math.min(this.targetPage, this.maxPage));
            this.targetPage = page;
            this.pageChanged(page);
        },

        
        toggleFilter(field, value) {
            if (this.dataList.filter[field] === value) {
                this.dataList.filter[field] = "";
                this.quickFilters[field] = null;
            } else {
                this.dataList.filter[field] = value;
                this.quickFilters[field] = value;
            }
            
            this.dataList.options.page = 1;
            this.load();
        },

        clearQuickFilters() {
	            this.quickFilters = { status: null, type: null, responsibility: null, archived: null };
	            this.dataList.filter.status = "";
	            this.dataList.filter.type = "";
	            this.dataList.filter.responsibility = "";
	            this.dataList.filter.archived = "";
	            this.load();
	        },

        pageChanged(page) {
            this.dataList.options.page = page;
            this.load();
        },

        pageSizeChanged(size) {
            this.dataList.options.itemsPerPage = size;
            this.load(true);
        },

        applyFilters() {
            this.dataList.options.page = 1;
            this.load();
        },

        filterDebounced: _.debounce(function () {
            this.applyFilters();
        }, 400),

        // A troca de tamanho e seguida de update:page(1), que recarrega.
        setPageSize(size) {
            this.dataList.options.itemsPerPage = size;
        },

        clearFilters() {
            Object.keys(this.dataList.filter).forEach(key => {
                this.dataList.filter[key] = "";
            });
            this.quickFilters = { status: null, type: null, responsibility: null, archived: null };
            this.applyFilters();
        },

	        async archiveItem(item) {
	            const proceed = await this.$dialog.confirm({
	                text: 'Deseja arquivar este pedido de hab/div?',
	                title: 'Arquivar pedido',
	                actions: { false: 'Cancelar', true: 'Arquivar' }
	            });

	            if (!proceed) return;

	            try {
	                await axios.patch(`/api/admin/user-events/archive/${item.id}`);
	                this.$dialog.notify.info('Pedido arquivado com sucesso.', { position: 'top-right', timeout: 2000 });
	                await this.load();
	            } catch (error) {
	                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
	            }
	        },

	        async unarchiveItem(item) {
	            const proceed = await this.$dialog.confirm({
	                text: 'Deseja desarquivar este pedido de hab/div?',
	                title: 'Desarquivar pedido',
	                actions: { false: 'Cancelar', true: 'Desarquivar' }
	            });

	            if (!proceed) return;

	            try {
	                await axios.patch(`/api/admin/user-events/unarchive/${item.id}`);
	                this.$dialog.notify.info('Pedido desarquivado com sucesso.', { position: 'top-right', timeout: 2000 });
	                await this.load();
	            } catch (error) {
	                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
	            }
	        },

	        getColorByStatus(status) {
            switch (status) {
                case 0: return 'warning'
                case 1: return 'accent'
                case 2: return 'error'
                case 3: return '#33691E'
                default: return 'grey';
            }
        },

        getChipColor(item) {
            if (item.currentReview && item.currentReview.status == 0) {
                return "#9FA8DA";
            }
            if (item.status == 4) {
                return "#7E55AB";
            }
            if (item.status == 5) {
                return "#29B6F6";
            }
            return this.getColorByStatus(item.status);
        },

        // Tom do ui-badge equivalente as cores de getChipColor.
        getChipTone(item) {
            if (item.currentReview && item.currentReview.status == 0) return "primary";
            switch (Number(item.status)) {
                case 0: return "warning";
                case 1: return "success";
                case 2: return "danger";
                case 3: return "success";
                case 4: return "purple";
                case 5: return "info";
                default: return "neutral";
            }
        },

        procurationWarning(procuration) {
            if (!procuration) return ''
            const who = procuration.procurator_name ? ` (${procuration.procurator_name})` : ''
            if (Number(procuration.status) === 3) return `Procuração com regularização solicitada${who}. Pedido apresentado em caráter provisório.`
            return Number(procuration.status) === 2
                ? `Procuração rejeitada${who}. Pedido apresentado em caráter provisório.`
                : `Procuração pendente de análise${who}. Pedido apresentado em caráter provisório.`
        },

        openProcurator(procuration) {
            if (!procuration || !procuration.procurator_user_id) return
            this.$router.push(`/procuradores/${procuration.procurator_user_id}`)
        },

        getChipLabel(item) {
            if (item.currentReview && item.currentReview.status == 0) {
                return "Em análise";
            }
            return this.enum(item.status, this.dataset.eventStatus, "id", "name");
        },

        enum(status, eventStatus, idKey, nameKey) {
            const matchedStatus = eventStatus.find((e) => e[idKey] === status);
            return matchedStatus ? matchedStatus[nameKey] : 'Desconhecido';
        },

        debounce: _.debounce(function () {
            this.reload();
        }, 500),

        reload: function () {
            this.load();
        },

        // Filtros da tela (formulario + filtros rapidos), no formato que a API espera.
        hasContradictory(item) {
            return !!(item.contradictory_reason && String(item.contradictory_reason).trim());
        },

        contradictoryOverdue(item) {
            const deadline = item.contradictory_deadline_at;
            if (!deadline || String(deadline).startsWith('0000-00-00')) return false;
            // Compara so o dia (YYYY-MM-DD) com a data local de hoje, sem depender de moment.
            const today = new Date();
            const todayIso = [today.getFullYear(), String(today.getMonth() + 1).padStart(2, '0'), String(today.getDate()).padStart(2, '0')].join('-');
            return String(deadline).slice(0, 10) < todayIso;
        },

        contradictoryTooltip(item) {
            const status = { 1: 'concordou', 2: 'discordou' }[Number(item.contradictory_status)];
            const when = item.contradictory_received_at ? ` em ${this.$options.filters.defaultDate(item.contradictory_received_at, 'DD/MM/YYYY HH:mm')}` : '';
            return `Contraditório recebido da devedora${when}${status ? ` (${status})` : ''}.`;
        },

        buildSearch() {
            const search = Object.fromEntries(Object.entries(this.dataList.filter).filter(([_, v]) => v !== "" && v !== null && v !== undefined));
            if (this.dataList.filter.status === 0) search.status = 0;
            return search;
        },

        // Exporta todos os pedidos que atendem aos filtros atuais (nao so a pagina).
        async exportXlsx() {
            this.exportLoading = true;
            try {
                const result = await axios.get('/api/admin/user-events/list', {
                    params: { page: 1, limit: 1000000, search: this.buildSearch(), with_items: 1 },
                });
                const data = (result.data.content && result.data.content.data) || [];
                if (!data.length) {
                    this.$dialog.notify.info("Nenhum pedido para exportar com os filtros aplicados.", { position: "top-right", timeout: 3000 });
                    return;
                }

                const fmtDate = (value, format = "DD/MM/YYYY HH:mm") => (value && value !== "0000-00-00 00:00:00" ? this.$options.filters.defaultDate(value, format) : "");
                const origem = { creditor: "Credor", debtor: "Devedora", aj: "Administração Judicial" };
                const contraditorio = { 0: "Pendente", 1: "Concordou", 2: "Discordou" };

                // Uma linha por item do pedido (valor e classe); pedido sem item vira uma linha so.
                const rows = [];
                data.forEach((event) => {
                    const isAudit = Number(event.type) === 90;
                    const credits = isAudit ? [] : ((event.items || []).filter((it) => Number(it.type) > 0));
                    const items = credits.length ? credits : [null];
                    const total = credits.reduce((sum, it) => sum + Number(it.amount || 0), 0);
                    items.forEach((it) => {
                        rows.push({
                            "Nº PEDIDO": event.id,
                            "TIPO": this.getEventTypeLabel(event.type),
                            "CREDOR": event.user ? event.user.name : (event.contact_name || ""),
                            "CPF/CNPJ": event.user ? event.user.registration : (event.contact_registration || ""),
                            "E-MAIL": event.user ? event.user.email : (event.contact_email || ""),
                            "CASO": event.client ? event.client.name : "",
                            "PROCESSO": event.client ? event.client.process_number : "",
                            "CLASSE": it ? (it.class_name || "") : "",
                            "VALOR PEDIDO": it ? Number(it.amount || 0) : "",
                            "VALOR DEFINIDO AJ": it && Number(it.partial_amount || 0) > 0 ? Number(it.partial_amount) : "",
                            "MOEDA": it ? this.enum(Number(it.currency || 1), currencyTypes, "id", "currencyFormat") : "",
                            "ORIGEM DO CRÉDITO": it ? (it.origin || "") : "",
                            "DEVEDORA": it ? (it.company_name || "") : "",
                            "TOTAL DO PEDIDO": credits.length ? total : "",
                            "STATUS": this.getChipLabel(event),
                            "ORIGEM DO PEDIDO": origem[event.submission_origin] || event.submission_origin || "",
                            "RESPONSÁVEL": event.responsible && event.responsible.name ? event.responsible.name : "",
                            "SOLICITADO EM": fmtDate(event.created_at),
                            "ANALISADO POR": event.admin && event.admin.name ? event.admin.name : "",
                            "ANALISADO EM": fmtDate(event.analyzed_at),
                            "CONTRADITÓRIO": event.contradictory_status !== null && event.contradictory_status !== undefined ? (contraditorio[event.contradictory_status] || event.contradictory_status) : "",
                            "PRAZO CONTRADITÓRIO": fmtDate(event.contradictory_deadline_at, "DD/MM/YYYY"),
                            "CONTRADITÓRIO RECEBIDO EM": fmtDate(event.contradictory_received_at),
                            "ARQUIVADO EM": fmtDate(event.archived_at),
                        });
                    });
                });

                const ok = await downloadXlsx(rows, `pedidos-hab-div-${this.$moment().format("YYYY-MM-DD")}`, { sheetName: "Pedidos" });
                if (ok) this.$dialog.notify.info(`Planilha gerada com ${data.length} pedido(s).`, { position: "top-right", timeout: 3000 });
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
            } finally {
                this.exportLoading = false;
            }
        },

        async load() {
            let search = this.buildSearch();
this.dataList.loading = true;
            var limit = this.dataList.options.itemsPerPage;

            try {
                const result = await axios.get('/api/admin/user-events/list', { 
                    params: { 
                        page: this.dataList.options.page, 
                        limit: limit, 
                        search: search
                    } 
                });

                this.dataList.data = result.data.content.data;
                this.dataList.count = result.data.content.paging.total;

            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
            } finally {
                this.dataList.loading = false;
            }
        },

        setFilterStatus(value) {
            this.dataList.filter.status = value;
        },

        getFilterCreditorTypes() {
            return [{ "id": "", "name": "Todos" }, ...this.dataset.creditorTypes]
        },

        async loadCreditorClasses() {
            const clientId = this.dataList.filter.client_id;

            // Sem caso escolhido o filtro fica desabilitado, entao basta manter
            // as pre-definidas para os rotulos da tabela.
            this.dataset.creditorTypes = clientId
                ? await creditorClassUtil.listByClient(clientId)
                : [...creditorClassUtil.predefined];

            if (!this.dataList.filter.creditor_type) return;

            const stillExists = this.dataset.creditorTypes
                .some((item) => Number(item.id) === Number(this.dataList.filter.creditor_type));

            // Trocar de caso pode invalidar a classe filtrada, o que deixaria a
            // listagem vazia sem explicacao.
            if (!stillExists) this.dataList.filter.creditor_type = "";
        },

        async loadClients() {
            try {
                const result = await axios.get(`/api/clients`, { params: { start: 1, limit: 100000, search: {} } });
                const data = result.data.content.data
                data.sort((a, b) => a.name.localeCompare(b.name));
                this.dataset.clients = [{ id: "", name: "Todos " }, ...data];
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 2000 })
            }
        },

        truncateText(text, maxLength) {
            if (text.length > maxLength) {
                return text.slice(0, maxLength) + '...';
            }
            return text;
        },

        viewItem(item) {
            this.$router.push(`divergencias/${item.id}`);
        },

        getEventDetailsHref(item) {
            return this.$router.resolve(`/divergencias/${item.id}`).href;
        },

        // "Editar" continua abrindo o detalhe em nova aba.
        openEventDetailsTab(item) {
            window.open(this.getEventDetailsHref(item), '_blank', 'noopener,noreferrer');
        },

        // A linha abre o detalhe so com a mesma permissao do "Editar".
        openRow(item) {
            if (this.$check('Event', 'GET')) this.viewItem(item);
        },


        async loadAdmins() {
            try {
                const result = await axios.get('/api/admin/list'); 
                const data = result.data.content.data;

                if (data && data.length) {
                    data.sort((a, b) => a.name.localeCompare(b.name));
                }

                this.dataset.admins = data || [];
                
            } catch (error) {
                console.error("Erro ao carregar admins", error);
                if (this.$dialog && this.$dialog.notify) {
                    this.$dialog.notify.error('Erro ao carregar lista de responsáveis');
                }
            }
        },

        actionAssign() {
            this.assignDialog = true;
        },

        actionBulkDeadline() {
            this.bulkDeadlineDialog = true;
        },

        async saveBulkDeadline() {
            if (!this.bulkDeadlineForm.deadline) {
                this.$dialog.notify.warning('Informe o prazo contraditório.', { position: 'top-right', timeout: 3000 });
                return;
            }

            const filters = this.bulkDeadlineFilters();
            const fmt = (value) => value ? value.split('-').reverse().join('/') : '';

            // Previa: quantos pedidos serao alterados com esses filtros.
            let preview = null;
            try {
                const countResult = await axios.post('/api/admin/user-events/mass-filter-count', { filters });
                preview = countResult.data && countResult.data.content ? countResult.data.content : null;
            } catch (error) {
                console.error(error);
            }

            if (preview && !preview.affected) {
                this.$dialog.notify.warning('Nenhum pedido corresponde aos filtros informados.', { position: 'top-right', timeout: 4000 });
                return;
            }

            const scope = filters.contradictory_deadline_on
                ? ` com prazo atual em ${fmt(filters.contradictory_deadline_on)}`
                : ' filtrados';
            const count = preview ? `${preview.affected} pedido(s)` : 'os pedidos';
            const proceedAction = await this.$dialog.confirm({
                text: `Aplicar o prazo ${fmt(this.bulkDeadlineForm.deadline)} a ${count}${scope}?`,
                title: 'Atribuir prazo em massa',
                actions: { false: 'Cancelar', true: 'Aplicar' }
            });

            if (!proceedAction) return;

            this.loading = true;

            try {
                const payload = {
                    contradictory_deadline_at: this.bulkDeadlineForm.deadline,
                    filters,
                };

                const result = await axios.post('/api/admin/user-events/bulk-contradictory-deadline', payload);
                const content = (result.data && result.data.content) || {};
                const updated = content.updated || 0;
                const notified = Array.isArray(content.notified) ? content.notified : [];

                // A recuperanda recebe e-mail a cada prazo aberto/retificado em lote.
                this.$dialog.notify.success(
                    `${updated} pedido(s) atualizado(s) com sucesso.${updated ? (notified.length ? ` Recuperanda notificada por e-mail (${notified.join(", ")}).` : " Nenhum e-mail de recuperanda cadastrado para o caso.") : ""}`,
                    { position: "top-right", timeout: 6000 }
                );
                this.bulkDeadlineDialog = false;
                await this.load();
            } catch (error) {
                console.error(error);
                this.$dialog.notify.error(this.$codes ? this.$codes(error) : 'Erro ao aplicar prazo em massa.');
            } finally {
                this.loading = false;
            }
        },

        // Com um dia informado, o filtro "Prazo atual" passa a ser "Com prazo" obrigatoriamente.
        onBulkDeadlineOnChange(value) {
            if (value) this.bulkDeadlineForm.hasDeadline = 1;
        },

        bulkDeadlineFilters() {
            return {
                client_id: this.bulkDeadlineForm.client_id,
                limit_count: this.bulkDeadlineForm.totalOrders,
                type: this.bulkDeadlineForm.type,
                status: this.bulkDeadlineForm.status,
                claim_class_id: this.bulkDeadlineForm.class,
                min_value: this.bulkDeadlineForm.minValue1,
                max_value: this.bulkDeadlineForm.minValue2,
                has_process: this.bulkDeadlineForm.hasProcess,
                court_uf: this.bulkDeadlineForm.hasProcess === 1 ? this.bulkDeadlineForm.courtUf : null,
                business_origin: this.bulkDeadlineForm.businessOrigin,
                is_contradictory: this.bulkDeadlineForm.contradictory,
                has_contradictory_deadline: this.bulkDeadlineForm.deadlineOn ? 1 : this.bulkDeadlineForm.hasDeadline,
                contradictory_deadline_on: this.bulkDeadlineForm.deadlineOn || null,
            };
        },

        redirectItem(item) {
            this.redirectForm = {
                item,
                responsible: null,
                loading: false,
            };
            this.redirectDialog = true;
        },

        closeRedirectDialog() {
            if (this.redirectForm.loading) return;

            this.redirectDialog = false;
            this.redirectForm = {
                item: null,
                responsible: null,
                loading: false,
            };
        },

        async saveRedirect() {
            if (!this.redirectForm.item || !this.redirectForm.responsible) {
                if (this.$dialog && this.$dialog.notify) {
                    this.$dialog.notify.error('Selecione um responsável.');
                }
                return;
            }

            this.redirectForm.loading = true;

            try {
                await axios.post('/api/admin/user-events/assign-responsible', {
                    responsible_admin_id: this.redirectForm.responsible,
                    filters: {
                        event_id: this.redirectForm.item.id,
                        only_unassigned: true,
                    },
                });

                if (this.$dialog && this.$dialog.notify) {
                    this.$dialog.notify.success('Responsável direcionado com sucesso.');
                }

                this.closeRedirectDialog();
                await this.load();
            } catch (error) {
                console.error(error);
                if (this.$dialog && this.$dialog.notify) {
                    this.$dialog.notify.error(this.$codes ? this.$codes(error) : 'Erro ao direcionar responsável.');
                }
            } finally {
                this.redirectForm.loading = false;
            }
        },

        actionReplace() {
            this.dialogReplace = true;
        },

        async saveAssignment() {
            this.loading = true;
            try {
                const payload = {
                    responsible_admin_id: this.assignForm.responsible,
                    filters: {
                        client_id: this.assignForm.client_id, 
                        limit_count: this.assignForm.totalOrders,
                        claim_class_id: this.assignForm.class,
                        min_value: this.assignForm.minValue1,
                        max_value: this.assignForm.minValue2,
                        has_process: this.assignForm.hasProcess,
                        court_uf: this.assignForm.hasProcess === 1 ? this.assignForm.courtUf : null,
                        business_origin: this.assignForm.businessOrigin,
                        is_contradictory: this.assignForm.contradictory,
                        only_unassigned: this.assignForm.withoutResponsible
                    }
                };

                await axios.post('/api/admin/user-events/assign-responsible', payload);
                if(this.$dialog && this.$dialog.notify) {
                    this.$dialog.notify.success('Responsável atribuído com sucesso');
                }
                
                this.assignDialog = false;
                this.load(); 
            } catch (error) {
                console.error(error);
                if(this.$dialog && this.$dialog.notify) {
                    this.$dialog.notify.error('Erro ao processar atribuição');
                }
            } finally {
                this.loading = false;
            }
        },

        async saveReplacement() {
            if (this.replaceForm.originResponsible === this.replaceForm.targetResponsible) {
                const msg = 'Os responsáveis de origem e destino devem ser diferentes';
                if(this.$dialog && this.$dialog.notify) this.$dialog.notify.error(msg);
                return;
            }

            this.loading = true;
            try {
                const payload = {
                    origin_admin_id: this.replaceForm.originResponsible,
                    target_admin_id: this.replaceForm.targetResponsible,
                    filters: {
                        limit_count: this.replaceForm.quantity,
                        has_process: this.replaceForm.hasProcess,
                        status: this.replaceForm.status
                    }
                };

                await axios.post('/api/admin/user-events/replace-responsible', payload);
                
                if(this.$dialog && this.$dialog.notify) {
                    this.$dialog.notify.success('Demandas transferidas com sucesso!');
                }
                
                this.dialogReplace = false;
                
                this.replaceForm = {
                    quantity: null,
                    hasProcess: null,
                    status: null,
                    originResponsible: null,
                    targetResponsible: null,
                };
                
                this.load(); 
            } catch (error) {
                console.error(error);
                if(this.$dialog && this.$dialog.notify) {
                    this.$dialog.notify.error('Erro ao transferir demandas');
                }
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>
